import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCourseCategories } from './useCourseCategories';
import { useSupabaseAuth } from './auth/supabaseAuth';
import { logger } from '@/utils/logger';

export interface DatabaseCourse {
  id: string;
  title: string;
  description?: string;
  category: string;
  level: string;
  duration_hours: number;
  total_modules: number;
  is_active: boolean;
}

export interface DatabaseCourseCategory {
  id: string;
  title: string;
  icon: any; // Keep for backward compatibility
  iconComponent: any;
  route: string;
  description: string;
  courses: DatabaseCourse[];
  courseCount: number;
}

/**
 * Hook for unified course navigation that uses database as primary source
 * with static configuration as fallback
 */
export const useDatabaseCourseNavigation = () => {
  const { categories: dbCategories, loading: categoriesLoading } = useCourseCategories();
  const { hasAccess } = useSupabaseAuth();
  const [dbCourses, setDbCourses] = useState<DatabaseCourse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch courses from database
  const fetchCourses = async (forceFresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      // Add timestamp to bust any Supabase client caching
      const timestamp = Date.now();
      
      const { data: courses, error: coursesError } = await supabase
        .from('courses')
        .select('id, title, description, category, level, duration_hours, total_modules, is_active')
        .eq('is_active', true)
        .order('title');

      if (coursesError) {
        logger.error('Failed to fetch courses:', coursesError);
        setError('Failed to load courses from database');
        // Don't return early - set empty array to allow fallback
        setDbCourses([]);
        return;
      }

      setDbCourses(courses || []);
      logger.info(`Successfully loaded ${courses?.length || 0} courses at ${new Date(timestamp).toISOString()}`);
    } catch (err) {
      logger.error('Error fetching courses:', err);
      setError('Network error loading courses');
      setDbCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Force fresh fetch on mount
    fetchCourses(true);
    
    // Refresh every 5 minutes (reduced from 30 seconds)
    const interval = setInterval(() => {
      fetchCourses(false);
    }, 300000);
    
    return () => clearInterval(interval);
  }, []);

  // Process database categories and courses (database-only approach)
  const unifiedCategories = useMemo((): DatabaseCourseCategory[] => {
    // Group courses by category
    const coursesByCategory = dbCourses.reduce((acc, course) => {
      if (!acc[course.category]) {
        acc[course.category] = [];
      }
      acc[course.category].push(course);
      return acc;
    }, {} as Record<string, DatabaseCourse[]>);

    // Create categories from database data only
    const categories: DatabaseCourseCategory[] = dbCategories.map(dbCategory => {
      const categoryCourses = coursesByCategory[dbCategory.id] || [];
      
      return {
        id: dbCategory.id,
        title: dbCategory.name,
        icon: dbCategory.iconComponent || dbCategory.icon, // For backward compatibility
        iconComponent: dbCategory.iconComponent || dbCategory.icon, // Use processed icon
        route: `/courses?category=${dbCategory.id}`,
        description: dbCategory.description || '',
        courses: categoryCourses,
        courseCount: categoryCourses.length
      };
    });

  // Show all categories and courses for course browsing
  // Authentication checks will be handled at the course detail level
  return categories.filter(category => category.courses.length > 0);
  }, [dbCategories, dbCourses, hasAccess]);

  // Get course route using database-driven routing
  const getCourseRoute = (courseId: string, categoryId: string): string => {
    return `/courses/${courseId}`;
  };

  // Get all courses flattened
  const allCourses = useMemo(() => {
    return unifiedCategories.flatMap(category => 
      category.courses.map(course => ({
        ...course,
        route: getCourseRoute(course.id, course.category),
        categoryName: category.title,
        categoryIcon: category.iconComponent
      }))
    );
  }, [unifiedCategories]);

  // Filter categories by course count
  const getCategoriesWithCourses = () => {
    return unifiedCategories.filter(category => category.courseCount > 0);
  };

  // Get courses by category
  const getCoursesByCategory = (categoryId: string) => {
    const category = unifiedCategories.find(cat => cat.id === categoryId);
    return category?.courses || [];
  };

  // Check if any course route is active
  const isCourseRouteActive = (pathname: string) => {
    return unifiedCategories.some(category => 
      pathname.startsWith(category.route) || 
      category.courses.some(course => pathname.includes(course.id))
    );
  };

  const totalCourses = allCourses.length;
  const totalCategories = unifiedCategories.length;

  return {
    categories: unifiedCategories,
    courses: allCourses,
    loading: loading || categoriesLoading,
    error,
    refetch: fetchCourses,
    getCourseRoute,
    getCategoriesWithCourses,
    getCoursesByCategory,
    isCourseRouteActive,
    totalCourses,
    totalCategories,
    hasAdminAccess: hasAccess('admin')
  };
};